import { useState } from "react";
import { MinusIcon, PlusIcon } from "../../../ui/icons";
import {
  useCategories,
  getPalette,
  getCustomCategories,
  getHiddenBuiltIn,
  addCategory,
  removeCategory,
  restoreCategory,
  updateCategory,
  setCategoryOptions,
  setWorkHoursTitle,
  type ColorPalette,
  type CategoryOptions,
} from "../../../shared/stores/category-store";

function ColorDot({ palette, selected, onClick }: {
  readonly palette: ColorPalette;
  readonly selected: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={palette.label}
      className={`w-6 h-6 rounded-full transition-all ${palette.dot} ${
        selected ? "ring-2 ring-offset-2 ring-offset-surface ring-accent scale-110" : "hover:scale-110"
      }`}
    />
  );
}

function HoursStepper({ value, onChange }: {
  readonly value: number | null;
  readonly onChange: (v: number | null) => void;
}) {
  const current = value ?? 0;
  const isActive = value !== null;

  return (
    <div className="flex items-center gap-1">
      {isActive ? (
        <>
          <button
            type="button"
            onClick={() => onChange(current <= 1 ? null : current - 1)}
            className="w-6 h-6 flex items-center justify-center rounded-md bg-surface border border-border text-ink-muted hover:text-ink text-sm font-medium"
          >
            <MinusIcon className="w-3 h-3" />
          </button>
          <span className="w-10 text-center text-xs font-semibold text-ink tabular-nums">
            {current}h
          </span>
          <button
            type="button"
            onClick={() => onChange(current + 1)}
            className="w-6 h-6 flex items-center justify-center rounded-md bg-surface border border-border text-ink-muted hover:text-ink text-sm font-medium"
          >
            <PlusIcon className="w-3 h-3" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => onChange(1)}
          className="px-2 py-1 text-[10px] font-medium text-ink-faint hover:text-ink border border-dashed border-border rounded-lg"
        >
          Définir
        </button>
      )}
    </div>
  );
}

function OptionsEditor({ options, onChange }: {
  readonly options: CategoryOptions;
  readonly onChange: (opts: CategoryOptions) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5 pt-2 border-t border-border/50">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink-muted">Afficher dans les heures</span>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={options.showInWorkHours}
            onChange={(e) => onChange({ ...options, showInWorkHours: e.target.checked })}
            className="sr-only"
          />
          <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center
            ${options.showInWorkHours
              ? "bg-accent border-accent"
              : "border-border hover:border-accent/50"
            }`}>
            {options.showInWorkHours && (
              <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </label>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink-muted">Objectif heures/sem.</span>
        <HoursStepper
          value={options.goalHours}
          onChange={(v) => onChange({ ...options, goalHours: v })}
        />
      </div>
    </div>
  );
}

function AddCategoryForm({ onDone }: { readonly onDone: () => void }) {
  const [label, setLabel] = useState("");
  const [colorId, setColorId] = useState("emerald");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const result = addCategory(trimmed, colorId);
    if (result === null) {
      setError("Ce nom existe déjà");
      return;
    }
    setLabel("");
    setError("");
    onDone();
  };

  return (
    <div className="flex flex-col gap-3 p-3 bg-surface-alt rounded-xl border border-border">
      <input
        type="text"
        value={label}
        onChange={(e) => { setLabel(e.target.value); setError(""); }}
        onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
        placeholder="Nom de la catégorie"
        autoFocus
        className="w-full px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60"
      />
      {error && <span className="text-[11px] text-danger">{error}</span>}
      <div className="flex gap-1.5 flex-wrap">
        {getPalette().map((p) => (
          <ColorDot key={p.id} palette={p} selected={colorId === p.id} onClick={() => setColorId(p.id)} />
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md">
          Annuler
        </button>
        <button type="button" onClick={handleSubmit} disabled={!label.trim()} className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90 disabled:opacity-40">
          Ajouter
        </button>
      </div>
    </div>
  );
}

function EditCategoryForm({ id, initialLabel, initialColorId, initialOptions, onDone }: {
  readonly id: string;
  readonly initialLabel: string;
  readonly initialColorId: string;
  readonly initialOptions: CategoryOptions;
  readonly onDone: () => void;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [colorId, setColorId] = useState(initialColorId);
  const [options, setOptions] = useState<CategoryOptions>(initialOptions);

  const handleSave = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    updateCategory(id, trimmed, colorId);
    setCategoryOptions(id, options);
    onDone();
  };

  return (
    <div className="flex flex-col gap-3 p-3 bg-surface-alt rounded-xl border border-accent/30">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
        autoFocus
        className="w-full px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60"
      />
      <div className="flex gap-1.5 flex-wrap">
        {getPalette().map((p) => (
          <ColorDot key={p.id} palette={p} selected={colorId === p.id} onClick={() => setColorId(p.id)} />
        ))}
      </div>
      <OptionsEditor options={options} onChange={setOptions} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md">
          Annuler
        </button>
        <button type="button" onClick={handleSave} className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90 disabled:opacity-40">
          Enregistrer
        </button>
      </div>
    </div>
  );
}

const BUILT_IN_IDS = new Set(["spiritual", "personal", "professional"]);
const BUILT_IN_COLORS: Record<string, string> = {
  spiritual: "purple",
  personal: "amber",
  professional: "blue",
};

export function CategoryManager() {
  const { categories, labels, dotColors, categoryOptions, workHoursTitle } = useCategories();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(workHoursTitle);

  const customCats = getCustomCategories();
  const customMap = new Map(customCats.map((c) => [c.id, c]));
  const hiddenBuiltIn = getHiddenBuiltIn();

  const handleSaveTitle = () => {
    setWorkHoursTitle(titleDraft.trim() || "Heures planifiées — semaine en cours");
    setEditingTitle(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Titre du widget heures planifiées */}
      <div className="flex flex-col gap-1.5 pb-2 border-b border-border/50">
        <span className="text-[11px] text-ink-faint">Titre du widget heures</span>
        {editingTitle ? (
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveTitle(); }}
              autoFocus
              className="flex-1 px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60"
            />
            <button type="button" onClick={handleSaveTitle} className="px-2 py-1 text-[10px] font-medium text-accent">
              OK
            </button>
            <button type="button" onClick={() => setEditingTitle(false)} className="px-2 py-1 text-[10px] text-ink-muted">
              Annuler
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setTitleDraft(workHoursTitle); setEditingTitle(true); }}
            className="text-left text-xs text-ink hover:text-accent transition-colors truncate"
          >
            {workHoursTitle}
          </button>
        )}
      </div>

      {/* Liste des catégories */}
      {categories.map((cat) => {
        const isEditing = editingId === cat;

        if (isEditing) {
          const custom = customMap.get(cat);
          return (
            <EditCategoryForm
              key={cat}
              id={cat}
              initialLabel={custom?.label ?? labels[cat]}
              initialColorId={custom?.colorId ?? BUILT_IN_COLORS[cat] ?? "emerald"}
              initialOptions={categoryOptions[cat] ?? { goalHours: null, showInWorkHours: true }}
              onDone={() => setEditingId(null)}
            />
          );
        }

        const opts = categoryOptions[cat];
        const goalInfo = opts?.goalHours ? `${opts.goalHours}h/sem` : null;

        return (
          <div key={cat} className="flex items-center gap-3 py-2 px-1">
            <span className={`w-3 h-3 rounded-full shrink-0 ${dotColors[cat]}`} />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-ink truncate block">{labels[cat]}</span>
              {goalInfo && (
                <span className="text-[10px] text-ink-faint">{goalInfo}</span>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setEditingId(cat)}
                className="px-2 py-1 text-[10px] font-medium text-ink-muted hover:text-ink rounded"
              >
                Modifier
              </button>
              <button
                type="button"
                onClick={() => removeCategory(cat)}
                className="px-2 py-1 text-[10px] font-medium text-danger/70 hover:text-danger rounded"
              >
                {BUILT_IN_IDS.has(cat) ? "Masquer" : "Supprimer"}
              </button>
            </div>
          </div>
        );
      })}

      {hiddenBuiltIn.length > 0 && (
        <div className="flex flex-col gap-2 pt-2 border-t border-border/50">
          <span className="text-[10px] text-ink-faint uppercase">Catégories masquées</span>
          {hiddenBuiltIn.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between py-1 px-1">
              <span className="text-xs text-ink-muted">{cat.label}</span>
              <button
                type="button"
                onClick={() => restoreCategory(cat.id)}
                className="px-2 py-1 text-[10px] font-medium text-accent hover:text-accent/80 rounded"
              >
                Restaurer
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd ? (
        <AddCategoryForm onDone={() => setShowAdd(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 py-2 px-1 text-xs font-medium text-accent hover:text-accent/80"
        >
          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent/10 text-accent"><PlusIcon className="w-3 h-3" /></span>
          Ajouter une catégorie
        </button>
      )}
    </div>
  );
}
